import React from 'react';
import styled from 'styled-components';

const DocsPage = styled.div`
  max-width: 800px;
  margin: 0 auto;
  
  h1 {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 2.5rem;
    font-weight: 700;
    color: #ffffff;
    margin-bottom: 1rem;
    line-height: 1.2;
  }
  
  h2 {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 1.8rem;
    font-weight: 600;
    color: #ffffff;
    margin: 2rem 0 1rem 0;
    line-height: 1.3;
  }
  
  h3 {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 1.4rem;
    font-weight: 600;
    color: #ffffff;
    margin: 1.5rem 0 0.75rem 0;
    line-height: 1.4;
  }
  
  p {
    font-family: 'Roboto', sans-serif;
    font-size: 1rem;
    line-height: 1.6;
    color: #e5e7eb;
    margin-bottom: 1rem;
  }
  
  ul, ol {
    margin: 1rem 0;
    padding-left: 1.5rem;
  }
  
  li {
    font-family: 'Roboto', sans-serif;
    font-size: 1rem;
    line-height: 1.6;
    color: #e5e7eb;
    margin-bottom: 0.5rem;
  }
  
  .feature-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 1.5rem;
    margin: 2rem 0;
  }
  
  .feature-card {
    background: rgba(168, 85, 247, 0.1);
    border-radius: 12px;
    padding: 1.5rem;
  }
  
  .feature-title {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 1.2rem;
    font-weight: 600;
    color: #a855f7;
    margin-bottom: 0.75rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  .step {
    background: rgba(168, 85, 247, 0.1);
    border-radius: 12px;
    padding: 1.5rem;
    margin: 1.5rem 0;
  }
  
  .step-number {
    background: #a855f7;
    color: #ffffff;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 0.875rem;
    margin-bottom: 1rem;
  }
  
  .step-title {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 1.2rem;
    font-weight: 600;
    color: #ffffff;
    margin-bottom: 0.75rem;
  }
  
  .step-content {
    font-family: 'Inter', sans-serif;
    color: #e5e7eb;
    line-height: 1.6;
  }
  
  .warning-box {
    background: rgba(245, 158, 11, 0.1);
    border-radius: 8px;
    padding: 1rem;
    margin: 1rem 0;
  }
  
  .warning-title {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-weight: 600;
    color: #f59e0b;
    margin-bottom: 0.5rem;
  }
  
  .warning-text {
    font-family: 'Inter', sans-serif;
    color: #d1d5db;
    font-size: 0.95rem;
  }
  
  .info-box {
    background: rgba(59, 130, 246, 0.1);
    border-radius: 8px;
    padding: 1rem;
    margin: 1rem 0;
  }
  
  .info-title {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-weight: 600;
    color: #3b82f6;
    margin-bottom: 0.5rem;
  }
  
  .info-text {
    font-family: 'Inter', sans-serif;
    color: #d1d5db;
    font-size: 0.95rem;
  }
  
  .contract-info {
    background: rgba(16, 185, 129, 0.1);
    border-radius: 12px;
    padding: 1.5rem;
    margin: 2rem 0;
  }
  
  .contract-title {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 1.2rem;
    font-weight: 600;
    color: #10b981;
    margin-bottom: 1rem;
  }
  
  .contract-address {
    font-family: 'monospace', sans-serif;
    font-size: 0.9rem;
    color: #ffffff;
    background: rgba(0, 0, 0, 0.3);
    padding: 0.75rem;
    border-radius: 8px;
    margin: 0.75rem 0;
    word-break: break-all;
  }
  
  @media (max-width: 768px) {
    h1 {
      font-size: 2rem;
    }
    
    h2 {
      font-size: 1.5rem;
    }
    
    .feature-grid {
      grid-template-columns: 1fr;
    }
  }
`;

const TokenLocker: React.FC = () => {
  return (
    <DocsPage>
      <h1>Token Locker System</h1>
      
      <p>
        The Mavrk Token Locker is a powerful, gas-efficient smart contract that allows users to lock any ERC-20 token for a specified period. This system provides secure, time-based token locking with no early withdrawal options, making it perfect for commitment strategies, vesting schedules, and long-term holding.
      </p>

      <div className="contract-info">
        <div className="contract-title">Contract Information</div>
        <p><strong>Contract Address:</strong></p>
        <div className="contract-address">0xd2C5947A6777500D9b7ad55a4D48AeF855AE6aBA</div>
        <p><strong>Network:</strong> Ink L2 (Chain ID: 57073)</p>
        <p><strong>Explorer:</strong> <a href="https://explorer.inkonchain.com/address/0xd2C5947A6777500D9b7ad55a4D48AeF855AE6aBA" target="_blank" rel="noopener noreferrer" style={{ color: '#60a5fa' }}>View on Explorer</a></p>
      </div>

      <h2>Key Features</h2>

      <div className="feature-grid">
        <div className="feature-card">
          <h3 className="feature-title">🔒 Flexible Locking</h3>
          <ul>
            <li>Lock any ERC-20 token on Ink L2</li>
            <li>Custom durations (minimum 1 day)</li>
            <li>Multiple locks per user per token</li>
            <li>Precise day-based calculations</li>
          </ul>
        </div>

        <div className="feature-card">
          <h3 className="feature-title">🛡️ Security Features</h3>
          <ul>
            <li>No early withdrawal (locks are final)</li>
            <li>Reentrancy protection</li>
            <li>SafeERC20 token transfers</li>
            <li>Comprehensive input validation</li>
          </ul>
        </div>

        <div className="feature-card">
          <h3 className="feature-title">⚡ Gas Efficiency</h3>
          <ul>
            <li>Optimized storage patterns</li>
            <li>Batch withdrawal operations</li>
            <li>Minimal gas overhead</li>
            <li>Free view functions</li>
          </ul>
        </div>
      </div>

      <h2>How to Use the Token Locker</h2>

      <div className="step">
        <div className="step-number">1</div>
        <div>
          <h3 className="step-title">Access the Token Locker</h3>
          <div className="step-content">
            <ol>
              <li>Navigate to the <strong>"Profile"</strong> section</li>
              <li>Scroll down to the <strong>"Wallet"</strong> component</li>
              <li>Click the <strong>"Lock"</strong> button at the top</li>
              <li>The Token Locker interface will open</li>
            </ol>
          </div>
        </div>
      </div>

      <div className="step">
        <div className="step-number">2</div>
        <div>
          <h3 className="step-title">Select a Token to Lock</h3>
          <div className="step-content">
            <ol>
              <li><strong>Search for tokens</strong> by name, symbol, or contract address</li>
              <li><strong>Choose from your holdings</strong> - tokens you've added to your wallet</li>
              <li><strong>Select the token</strong> you want to lock</li>
              <li>The interface will show the token details and your balance</li>
            </ol>
          </div>
        </div>
      </div>

      <div className="step">
        <div className="step-number">3</div>
        <div>
          <h3 className="step-title">Set Lock Parameters</h3>
          <div className="step-content">
            <h4>Amount to Lock</h4>
            <ul>
              <li><strong>Manual Input:</strong> Enter the exact amount you want to lock</li>
              <li><strong>Percentage Buttons:</strong> Use 25%, 50%, 75%, or Max for quick selection</li>
              <li><strong>Balance Display:</strong> See your current token balance</li>
              <li><strong>USD Value:</strong> Real-time USD value of the amount</li>
            </ul>

            <h4>Lock Duration</h4>
            <ul>
              <li><strong>Days Input:</strong> Enter the number of days to lock (minimum 1 day)</li>
              <li><strong>Common Durations:</strong> 7, 30, 90, 180, 365 days</li>
              <li><strong>Custom Periods:</strong> Any number of days you prefer</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="step">
        <div className="step-number">4</div>
        <div>
          <h3 className="step-title">Approve and Lock</h3>
          <div className="step-content">
            <ol>
              <li><strong>Approve Tokens:</strong> First transaction to approve the locker contract</li>
              <li><strong>Create Lock:</strong> Second transaction to lock the tokens</li>
              <li><strong>Confirmation:</strong> Tokens are transferred to the locker contract</li>
              <li><strong>Lock ID:</strong> You'll receive a unique lock ID for tracking</li>
            </ol>
          </div>
        </div>
      </div>

      <h2>Lock Management</h2>

      <div className="step">
        <div className="step-number">5</div>
        <div>
          <h3 className="step-title">Viewing Your Locks</h3>
          <div className="step-content">
            <p>In the Profile section, you can view all your active locks:</p>
            <ul>
              <li><strong>Lock ID:</strong> Unique identifier for each lock</li>
              <li><strong>Token:</strong> The locked token name and symbol</li>
              <li><strong>Amount Locked:</strong> Exact amount of tokens locked</li>
              <li><strong>Lock Duration:</strong> Total days the tokens are locked</li>
              <li><strong>Days Remaining:</strong> Days until the lock expires</li>
              <li><strong>Status:</strong> Locked, Unlocked, or Withdrawn</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="step">
        <div className="step-number">6</div>
        <div>
          <h3 className="step-title">Withdrawing Tokens</h3>
          <div className="step-content">
            <h4>Individual Withdrawal</h4>
            <ol>
              <li>Find the lock you want to withdraw</li>
              <li>Click <strong>"Withdraw"</strong> (only available when lock expires)</li>
              <li>Confirm the transaction</li>
              <li>Tokens are transferred back to your wallet</li>
            </ol>

            <h4>Batch Withdrawal</h4>
            <ol>
              <li>Use <strong>"Withdraw All"</strong> for a specific token</li>
              <li>All expired locks for that token are withdrawn</li>
              <li>Gas-efficient single transaction</li>
              <li>All tokens transferred at once</li>
            </ol>
          </div>
        </div>
      </div>

      <h2>Important Considerations</h2>

      <div className="warning-box">
        <div className="warning-title">⚠️ No Early Withdrawal</div>
        <div className="warning-text">
          <ul>
            <li><strong>Locks are FINAL</strong> - there is no early withdrawal option</li>
            <li><strong>Plan carefully</strong> - only lock tokens you can afford to lose access to</li>
            <li><strong>Emergency situations</strong> - consider this before locking large amounts</li>
          </ul>
        </div>
      </div>

      <div className="info-box">
        <div className="info-title">💰 Token Amounts</div>
        <div className="info-text">
          <ul>
            <li><strong>Full Decimals Required:</strong> Enter amounts with full decimal precision</li>
            <li><strong>Example:</strong> For 100 tokens with 18 decimals, enter <code>100000000000000000000</code></li>
            <li><strong>Check Decimals:</strong> Verify the token's decimal places before locking</li>
          </ul>
        </div>
      </div>

      <h2>Use Cases</h2>

      <div className="feature-grid">
        <div className="feature-card">
          <h3 className="feature-title">🎯 Commitment Strategies</h3>
          <ul>
            <li>Long-term holding to prevent impulsive selling</li>
            <li>Investment discipline enforcement</li>
            <li>DCA (Dollar Cost Averaging) strategies</li>
          </ul>
        </div>

        <div className="feature-card">
          <h3 className="feature-title">🏢 Vesting and Rewards</h3>
          <ul>
            <li>Team token vesting with gradual release</li>
            <li>Reward distribution until conditions are met</li>
            <li>Staking rewards locking for additional periods</li>
          </ul>
        </div>

        <div className="feature-card">
          <h3 className="feature-title">💼 Portfolio Management</h3>
          <ul>
            <li>Risk management for volatile tokens</li>
            <li>Diversification with different lock periods</li>
            <li>Tax planning for specific periods</li>
          </ul>
        </div>
      </div>

      <h2>Best Practices</h2>

      <div className="step">
        <div className="step-number">7</div>
        <div>
          <h3 className="step-title">Before Locking</h3>
          <div className="step-content">
            <ol>
              <li><strong>Verify Token Address:</strong> Ensure you're locking the correct token</li>
              <li><strong>Check Decimals:</strong> Confirm the token's decimal places</li>
              <li><strong>Test with Small Amount:</strong> Try with a small amount first</li>
              <li><strong>Plan Duration:</strong> Choose appropriate lock periods</li>
              <li><strong>Consider Alternatives:</strong> Ensure you won't need the tokens</li>
            </ol>
          </div>
        </div>
      </div>

      <div className="step">
        <div className="step-number">8</div>
        <div>
          <h3 className="step-title">After Lock Expires</h3>
          <div className="step-content">
            <ol>
              <li><strong>Withdraw Promptly:</strong> Don't leave tokens in the contract unnecessarily</li>
              <li><strong>Batch Withdrawals:</strong> Use batch withdrawal for gas efficiency</li>
              <li><strong>Verify Amounts:</strong> Confirm you receive the expected amount</li>
              <li><strong>Update Records:</strong> Update your portfolio tracking</li>
            </ol>
          </div>
        </div>
      </div>

      <div className="info-box">
        <div className="info-title">Pro Tip</div>
        <div className="info-text">
          Use the portfolio overview to track your financial progress and make informed decisions about your token holdings and locking strategies. The wallet system is designed to give you complete control over your DeFi assets.
        </div>
      </div>
    </DocsPage>
  );
};

export default TokenLocker;
